import { WinterConceptLandingViewModel } from '@api/models/WinterConceptLandingViewModel';
import { ApiRequestOptions } from '@api/core/ApiRequestOptions';
import { getRequestOptions } from '@api/core/utils';
import getBaseURL from '@utils/api/getBaseURL';

import type { BaseMetaViewModel } from '../models/BaseMetaViewModel';
import type { CampaignBannerViewModel } from '../models/CampaignBannerViewModel';
import type { CampaignDetailsLandingViewModel } from '../models/CampaignDetailsLandingViewModel';
import type { CampaignPromotionListViewModel } from '../models/CampaignPromotionListViewModel';
import type { CampaignPromotionViewModel } from '../models/CampaignPromotionViewModel';
import type { CategoryPromotionViewModel } from '../models/CategoryPromotionViewModel';
import type { ConceptHotelDetailViewModel } from '../models/ConceptHotelDetailViewModel';
import type { CorporateAboutUsPageViewModel } from '../models/CorporateAboutUsPageViewModel';
import type { CorporateAgenciesPageViewModel } from '../models/CorporateAgenciesPageViewModel';
import type { CorporateSeturYayinlariPageViewModel } from '../models/CorporateSeturYayinlariPageViewModel';
import type { DocumentViewModel } from '../models/DocumentViewModel';
import type { FlightLandingViewModel } from '../models/FlightLandingViewModel';
import type { FooterViewModel } from '../models/FooterViewModel';
import type { HeaderAndfooterViewModel } from '../models/HeaderAndfooterViewModel';
import type { HeaderViewModel } from '../models/HeaderViewModel';
import type { HeroCampaignV2ViewModel } from '../models/HeroCampaignV2ViewModel';
import type { HeroCampaignViewModel } from '../models/HeroCampaignViewModel';
import type { HomePageCampaignBannerViewModel } from '../models/HomePageCampaignBannerViewModel';
import type { HomeProductPromotionViewModel } from '../models/HomeProductPromotionViewModel';
import type { HotelLandingViewModel } from '../models/HotelLandingViewModel';
import type { LoginSignUpViewModel } from '../models/LoginSignUpViewModel';
import type { MobileAppPromotionsViewModel } from '../models/MobileAppPromotionsViewModel';
import type { MobileStoriesViewModel } from '../models/MobileStoriesViewModel';
import type { PaymentOptionsDetailViewModel } from '../models/PaymentOptionsDetailViewModel';
import type { ProductPromotionViewModel } from '../models/ProductPromotionViewModel';
import type { SetairPageViewModel } from '../models/SetairPageViewModel';
import type { SeturVizePageViewModel } from '../models/SeturVizePageViewModel';
import type { SocialMediaViewModel } from '../models/SocialMediaViewModel';
import type { StaticPagesSidebarViewModel } from '../models/StaticPagesSidebarViewModel';
import type { StaticPagesViewModel } from '../models/StaticPagesViewModel';
import type { ThemeLandingPageViewModel } from '../models/ThemeLandingPageViewModel';
import type { TravelSuggestionsViewModel } from '../models/TravelSuggestionsViewModel';
import type { WhySeturViewModel } from '../models/WhySeturViewModel';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class ContentsService {
  /**
   * Meta
   * Gets meta infos for SEO
   * @returns BaseMetaViewModel Success
   * @throws ApiError
   */
  public static async getMeta(
    {
      slug,
    }: {
      slug: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<BaseMetaViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<BaseMetaViewModel>({
      method: 'GET',
      url: `/contents/meta`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Header
   * Gets header
   * @returns HeaderViewModel Success
   * @throws ApiError
   */
  public static async header(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<HeaderViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<HeaderViewModel>({
      method: 'GET',
      url: `/contents/header`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Campaigns Banner
   * Gets banner of campaign
   * @returns CampaignBannerViewModel Success
   * @throws ApiError
   */
  public static async campaignsBanner(
    {
      position,
      type,
    }: {
      /**
       * Position: top or bottom, Default: top
       */
      position?: string;
      /**
       * Type: hotel,tour,flight,international flight,mainpage default: mainpage
       */
      type?: any;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<CampaignBannerViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<CampaignBannerViewModel>({
      method: 'GET',
      url: `/contents/campaigns/banner`,
      query: {
        ...(position ? { position: position } : {}),
        ...(type ? { type: type } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Campaigns Promotion
   * Gets promotion of campaign
   * @returns CampaignPromotionListViewModel Success
   * @throws ApiError
   */
  public static async campaignsPromotion(
    {
      page,
      size,
    }: {
      page?: number;
      size?: number;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<CampaignPromotionListViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<CampaignPromotionListViewModel>({
      method: 'GET',
      url: `/contents/campaigns/promotion`,
      query: {
        ...(page ? { page: page } : {}),
        ...(size ? { size: size } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Campaigns Hero
   * Gets hero banner of campaign
   * @returns HeroCampaignViewModel Success
   * @throws ApiError
   */
  public static async campaignsHero(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<HeroCampaignViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<HeroCampaignViewModel>({
      method: 'GET',
      url: `/contents/campaigns/hero`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Promotions Product
   * Gets promotions of product
   * @returns ProductPromotionViewModel Success
   * @throws ApiError
   */
  public static async promotionsProduct(
    {
      type,
    }: {
      /**
       * Type: hotel or tour, default: hotel
       */
      type: any;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<ProductPromotionViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<ProductPromotionViewModel>({
      method: 'GET',
      url: `/contents/promotions/product`,
      query: {
        ...(type ? { type: type } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Category Promotions
   * Get category promotions by type
   * @returns CategoryPromotionViewModel Success
   * @throws ApiError
   */
  public static async categoryPromotions(
    {
      type,
    }: {
      /**
       * Type: hotel, expedia hotels, tour or cruise default: hotel
       */
      type: any;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<CategoryPromotionViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<CategoryPromotionViewModel>({
      method: 'GET',
      url: `/contents/promotions/category`,
      query: {
        ...(type ? { type: type } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Home Promotions Product
   * Gets promotions of product for homepage
   * @returns HomeProductPromotionViewModel Success
   * @throws ApiError
   */
  public static async homePromotionsProduct(
    {
      type,
    }: {
      /**
       * Type: one_cikan_oteller, one_cikan_turlar or one_cikan_yurtici_turlar, default: one_cikan_oteller
       */
      type: any;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<HomeProductPromotionViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<HomeProductPromotionViewModel>({
      method: 'GET',
      url: `/contents/home/promotions/product`,
      query: {
        ...(type ? { type: type } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Home Promotions Category
   * Gets promotions of category for homepage
   * @returns CategoryPromotionViewModel Success
   * @throws ApiError
   */
  public static async homePromotionsCategory(
    {
      type,
    }: {
      /**
       * Type: one_cikan_tur_kategorileri, one_cikan_gemi_kategorileri, one_cikan_otel_kategorileri or one_cikan_yurt_disi_otel_kategorileri default: one_cikan_tur_kategorileri
       */
      type: any;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<CategoryPromotionViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<CategoryPromotionViewModel>({
      method: 'GET',
      url: `/contents/home/promotions/category`,
      query: {
        ...(type ? { type: type } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Footer
   * Gets footer
   * @returns FooterViewModel Success
   * @throws ApiError
   */
  public static async footer(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<FooterViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<FooterViewModel>({
      method: 'GET',
      url: `/contents/footer`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Sidebar
   * Gets sidebar for static pages,
   * @returns StaticPagesSidebarViewModel Success
   * @throws ApiError
   */
  public static async getSidebar(
    {
      type,
    }: {
      /**
       * Type: setur, corporate
       */
      type?: any;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<StaticPagesSidebarViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<StaticPagesSidebarViewModel>({
      method: 'GET',
      url: `/contents/sidebar`,
      query: {
        ...(type ? { type: type } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Document
   * Gets document url or text by type
   * @returns DocumentViewModel Success
   * @throws ApiError
   */
  public static async getDocument(
    {
      type,
    }: {
      /**
       * Document Type (AgreementDoc, ConsentDoc, ConsentText,LoyaltyText)
       */
      type: any;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<DocumentViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<DocumentViewModel>({
      method: 'GET',
      url: getBaseURL(`/v2/contents/document`, `ContentsService`),
      query: {
        ...(type ? { type: type } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Static Pages
   * Gets static pages by slug
   * @returns StaticPagesViewModel Success
   * @throws ApiError
   */
  public static async getStaticPages(
    {
      slug,
    }: {
      slug: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<StaticPagesViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<StaticPagesViewModel>({
      method: 'GET',
      url: `/contents/static`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Static Pages (Setair)
   * Gets static page for setair
   * @returns SetairPageViewModel Success
   * @throws ApiError
   */
  public static async getSetair(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SetairPageViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<SetairPageViewModel>({
      method: 'GET',
      url: `/contents/static/setair`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Static Pages (Setur Vize)
   * Gets static page for setur vize
   * @returns SeturVizePageViewModel Success
   * @throws ApiError
   */
  public static async getSeturVize(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SeturVizePageViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<SeturVizePageViewModel>({
      method: 'GET',
      url: `/contents/static/setur-vize`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Static Pages (Hakkimizda)
   * Gets static page for about us
   * @returns CorporateAboutUsPageViewModel Success
   * @throws ApiError
   */
  public static async getAboutUs(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<CorporateAboutUsPageViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<CorporateAboutUsPageViewModel>({
      method: 'GET',
      url: `/contents/static/hakkimizda`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Static Pages (Agencies)
   * Gets static page for agencies
   * @returns CorporateAgenciesPageViewModel Success
   * @throws ApiError
   */
  public static async getAgencies(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<CorporateAgenciesPageViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<CorporateAgenciesPageViewModel>({
      method: 'GET',
      url: `/contents/static/agencies`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Static Pages (Setur Yayinlari)
   * Gets static page for setur yayinlari
   * @returns CorporateSeturYayinlariPageViewModel Success
   * @throws ApiError
   */
  public static async getSeturYayinlari(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<CorporateSeturYayinlariPageViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<CorporateSeturYayinlariPageViewModel>({
      method: 'GET',
      url: `/contents/static/seturyayinlari`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Login Signup
   * Gets content for login-signup pages
   * @returns LoginSignUpViewModel Success
   * @throws ApiError
   */
  public static async getLoginSignup(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<LoginSignUpViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<LoginSignUpViewModel>({
      method: 'GET',
      url: `/contents/loginsignup`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Hotels (Landing)
   * Gets hotel landing pages by slug
   * @returns HotelLandingViewModel Success
   * @throws ApiError
   */
  public static async getHotelLanding(
    {
      slug,
    }: {
      slug: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<HotelLandingViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<HotelLandingViewModel>({
      method: 'GET',
      url: `/contents/landing/hotel`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Flights (Landing)
   * Gets flight landing pages by slug
   * @returns FlightLandingViewModel Success
   * @throws ApiError
   */
  public static async getFlightLanding(
    {
      slug,
    }: {
      slug: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<FlightLandingViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<FlightLandingViewModel>({
      method: 'GET',
      url: `/contents/landing/flight`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Flight-City (Landing)
   * Gets flight-city landing pages by slug
   * @returns FlightLandingViewModel Success
   * @throws ApiError
   */
  public static async getFlightCityLanding(
    {
      slug,
    }: {
      slug: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<FlightLandingViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<FlightLandingViewModel>({
      method: 'GET',
      url: `/contents/landing/flight-city`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Campaigns (Landing)
   * Gets campaigns for landing pages by slug
   * @returns CampaignPromotionViewModel Success
   * @throws ApiError
   */
  public static async getCampaigns(
    {
      slug,
    }: {
      /**
       * Examples: turk-telekom-kampanyalari
       */
      slug: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<CampaignPromotionViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<CampaignPromotionViewModel>({
      method: 'GET',
      url: `/contents/campaigns`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * WinterConcept (Landing)
   * Gets Winter concepts for landing pages by slug
   * @returns WinterConceptLandingViewModel Success
   * @throws ApiError
   */
  public static async getWinterConcept(
    {
      slug,
    }: {
      /**
       * Examples: kis-tatili
       */
      slug: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<WinterConceptLandingViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<WinterConceptLandingViewModel>({
      method: 'GET',
      url: `/contents/landing/themes`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Campaign Details (Landing)
   * Gets campaign detail landing pages by slug
   * @returns CampaignDetailsLandingViewModel Success
   * @throws ApiError
   */
  public static async getCampaignDetails(
    {
      slug,
    }: {
      /**
       * Examples: kismi-odeme-kampanyasi
       */
      slug: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<CampaignDetailsLandingViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<CampaignDetailsLandingViewModel>({
      method: 'GET',
      url: `/contents/campaigns/detail`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Payment Options Details (Landing)
   * Gets payment options details landing pages by slug
   * @returns PaymentOptionsDetailViewModel Success
   * @throws ApiError
   */
  public static async getPaymentDetails(
    {
      slug,
    }: {
      /**
       * Examples: odeme-secenekleri-akbank, odeme-secenekleri-yapi-kredi
       */
      slug: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<PaymentOptionsDetailViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<PaymentOptionsDetailViewModel>({
      method: 'GET',
      url: `/contents/payments/detail`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Payment Options
   * Gets payment options
   * @returns CampaignPromotionViewModel Success
   * @throws ApiError
   */
  public static async getPayments(
    {
      slug,
    }: {
      /**
       * Examples: odeme-secenekleri
       */
      slug: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<CampaignPromotionViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<CampaignPromotionViewModel>({
      method: 'GET',
      url: `/contents/payments`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * @deprecated
   * Seo (Main Page)
   * Gets seo content for main page
   * @returns BaseMetaViewModel Success
   * @throws ApiError
   */
  public static async getMainPageSeo(
    {
      slug,
    }: {
      slug: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<BaseMetaViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<BaseMetaViewModel>({
      method: 'GET',
      url: `/contents/seo`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * @deprecated
   * Contents (Static Announcements)
   * Gets content for static announcements
   * @returns BaseMetaViewModel Success
   * @throws ApiError
   */
  public static async getAnnouncements(
    {
      slug,
    }: {
      slug: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<BaseMetaViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<BaseMetaViewModel>({
      method: 'GET',
      url: `/contents/announcements`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Contents (Social Media)
   * Gets content for static social media icons
   * @returns SocialMediaViewModel Success
   * @throws ApiError
   */
  public static async getSocialMedia(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SocialMediaViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<SocialMediaViewModel>({
      method: 'GET',
      url: `/contents/socialmedia`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Contents (Why Setur)
   * Gets content for why setur field
   * @returns WhySeturViewModel Success
   * @throws ApiError
   */
  public static async getWhySetur(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<WhySeturViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<WhySeturViewModel>({
      method: 'GET',
      url: `/contents/why-setur`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Contents (Travel Suggestions)
   * Gets content for travel suggestions
   * @returns TravelSuggestionsViewModel Success
   * @throws ApiError
   */
  public static async getTravelSuggestions(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<TravelSuggestionsViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<TravelSuggestionsViewModel>({
      method: 'GET',
      url: `/contents/travel-suggestions`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Contents (Mobile Stories)
   * Gets content for mobile stories
   * @returns MobileStoriesViewModel Success
   * @throws ApiError
   */
  public static async getMobileStories(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<MobileStoriesViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<MobileStoriesViewModel>({
      method: 'GET',
      url: `/contents/mobile-stories`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Contents (Hero Parallax)
   * Gets content for parallax item
   * @returns HeroCampaignV2ViewModel Success
   * @throws ApiError
   */
  public static async getHeroParallax(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<HeroCampaignV2ViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<HeroCampaignV2ViewModel>({
      method: 'GET',
      url: `/contents/hero-parallax`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Contents (Header and Footer)
   * Gets content for header and footer
   * @returns HeaderAndfooterViewModel Success
   * @throws ApiError
   */
  public static async getHeaderAndFooter(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<HeaderAndfooterViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<HeaderAndfooterViewModel>({
      method: 'GET',
      url: `/contents/headerandfooter`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Contents (Concept Hotel Detail)
   * Gets content for concept hotel details.
   * @returns ConceptHotelDetailViewModel Success
   * @throws ApiError
   */
  public static async getConceptHotelDetails(
    {
      slug,
    }: {
      slug: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<ConceptHotelDetailViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<ConceptHotelDetailViewModel>({
      method: 'GET',
      url: `/contents/concepthotels`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Themes (Theme Landing Pages)
   * Gets content for themes landing pages.
   * @returns ThemeLandingPageViewModel Success
   * @throws ApiError
   */
  public static async getThemeLanding(
    {
      slug,
    }: {
      slug?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<ThemeLandingPageViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<ThemeLandingPageViewModel>({
      method: 'GET',
      url: `/contents/landing/themes`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   *  Home Campaigns (Home Campaign Banners)
   * Gets content for home campaign banner.
   * @returns HomePageCampaignBannerViewModel Success
   * @throws ApiError
   */
  public static async getHomeCampaignBanners(
    {
      slug,
    }: {
      /**
       * Position: top or bottom, Default: top
       */
      slug?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<HomePageCampaignBannerViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<HomePageCampaignBannerViewModel>({
      method: 'GET',
      url: `/contents/home/campaigns/banners`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   *  Mobile Promotions
   * Gets content for mobile promotions.
   * @returns MobileAppPromotionsViewModel Success
   * @throws ApiError
   */
  public static async getMobilePromotions(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<MobileAppPromotionsViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<MobileAppPromotionsViewModel>({
      method: 'GET',
      url: getBaseURL(`/v2/contents/mobile-promotions`, `ContentsService`),
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }
}
